import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { HOME } from "../constants/testIds";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const HERO = "https://images.pexels.com/photos/773842/pexels-photo-773842.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function HomePage() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [callData, setCallData] = useState({ name: "", phone: "" });
  const [callSubmitted, setCallSubmitted] = useState(false);
  const [callLoading, setCallLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard");
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { code: code.trim() }, { withCredentials: true });
      login(data.user);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid access code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = async (e) => {
    e.preventDefault();
    setCallLoading(true);
    try {
      await axios.post(`${API}/public/call-request`, callData);
      setCallSubmitted(true);
    } catch {
      setCallData((d) => ({ ...d, _error: "Unable to submit. Please try again." }));
    } finally {
      setCallLoading(false);
    }
  };

  const handleModalClose = (open) => {
    setShowModal(open);
    if (!open) {
      setTimeout(() => { setCallSubmitted(false); setCallData({ name: "", phone: "" }); }, 300);
    }
  };

  return (
    <div className="relative min-h-screen bg-black overflow-hidden" data-testid={HOME.hero}>
      {/* Hero background */}
      <div className="absolute inset-0">
        <img src={HERO} alt="" className="w-full h-full object-cover object-center opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/85" />
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="w-full max-w-xs text-center"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="mb-14 flex justify-center"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_investment-portal-38/artifacts/5nykqmge_Green%20and%20Gold%20Professional%20Letter%20A%20Logo.png"
              alt="Scarlett Properties"
              className="w-40 h-auto"
              style={{ mixBlendMode: "screen" }}
            />
          </motion.div>

          {/* Label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-[9px] tracking-[0.45em] text-white uppercase mb-9 font-light opacity-100"
          >
            Private Access
          </motion.p>

          {/* Access Form */}
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            onSubmit={handleLogin}
          >
            <input
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
              placeholder="Insert your SP Code"
              data-testid={HOME.accessCodeInput}
              className="w-full bg-transparent border-b border-white/20 text-white text-center text-sm font-mono tracking-[0.25em] py-3 placeholder:text-white placeholder:opacity-100 focus:outline-none focus:border-white/60 transition-colors duration-500"
              autoComplete="off"
              spellCheck="false"
            />

            <AnimatePresence>
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-[#C8102E] text-[10px] tracking-wider mt-3 font-light"
                  data-testid={HOME.errorMessage}
                >
                  {error}
                </motion.p>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={loading || !code.trim()}
              data-testid={HOME.accessCodeSubmit}
              className="w-full mt-7 bg-[#C8102E] hover:bg-[#A50D25] text-white text-[10px] tracking-[0.45em] uppercase py-4 transition-colors duration-300 font-bold"
            >
              {loading ? "Verifying..." : "Access"}
            </button>
          </motion.form>

          {/* Confidential Call CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-11"
          >
            <button
              onClick={() => setShowModal(true)}
              data-testid={HOME.confidentialCallLink}
              className="text-[9px] tracking-[0.35em] text-white opacity-100 uppercase border-b border-white/20 hover:border-white/50 transition-all duration-300 pb-px font-light"
            >
              Request a Confidential Call
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Confidential Call Modal */}
      <Dialog open={showModal} onOpenChange={handleModalClose}>
        <DialogContent
          className="bg-[#080808] border border-zinc-800 text-white max-w-sm p-8"
          data-testid={HOME.callModal}
        >
          <DialogHeader className="mb-6">
            <div className="w-6 h-px bg-[#C8102E] mb-5" />
            <DialogTitle className="font-serif text-2xl font-light tracking-wide text-white">
              Confidential Call
            </DialogTitle>
            <DialogDescription className="text-xs text-white tracking-[0.15em] mt-2 font-light leading-relaxed">
              A member of our advisory team will contact you discreetly.
            </DialogDescription>
          </DialogHeader>

          {!callSubmitted ? (
            <form onSubmit={handleCall} className="space-y-7">
              <div>
                <input
                  type="text"
                  value={callData.name}
                  onChange={(e) => setCallData((d) => ({ ...d, name: e.target.value }))}
                  placeholder="Full Name"
                  data-testid={HOME.callModalName}
                  required
                  className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm placeholder:text-white/30 focus:outline-none focus:border-white/60 transition-colors font-light tracking-wide"
                />
              </div>
              <div>
                <input
                  type="tel"
                  value={callData.phone}
                  onChange={(e) => setCallData((d) => ({ ...d, phone: e.target.value }))}
                  placeholder="Mobile Number"
                  data-testid={HOME.callModalPhone}
                  required
                  className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm placeholder:text-white/30 focus:outline-none focus:border-white/60 transition-colors font-light tracking-wide"
                />
              </div>
              {callData._error && <p className="text-[#C8102E] text-xs">{callData._error}</p>}
              <button
                type="submit"
                disabled={callLoading}
                data-testid={HOME.callModalSubmit}
                className="w-full bg-[#C8102E] hover:bg-[#A50D25] text-white text-[10px] tracking-[0.4em] uppercase py-4 transition-colors duration-300 disabled:opacity-50 mt-2 font-light"
              >
                {callLoading ? "Submitting..." : "Submit"}
              </button>
            </form>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-6 text-center space-y-3"
            >
              <div className="w-8 h-px bg-[#C8102E] mx-auto" />
              <p className="text-white text-sm tracking-wider font-light">Request received.</p>
              <p className="text-white text-xs tracking-wider font-light">We will be in touch shortly.</p>
            </motion.div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
